// `POST /v1/realtime/token` — a realtime token for the signed-in person (spec/09 §1; INFRA-18, #379).
//
// The gateway authenticates every socket with a realtime token in the first frame, never in the URL.
// Staff and players get one here, with their session; a station gets one from the device token
// exchange (spec/07 §5, F-STN-05). A JWT signed with the api's JWKS (better-auth's `jwt` plugin,
// EdDSA), five minutes, refreshed through this endpoint when `auth.expiring` arrives.
//
//   { "organizationId": "org_…" }   a member token for that organisation: `sub` is the membership
//                                   (`mem_…`, the `member:{id}` channel), with the venues the member
//                                   works at and the channel permissions their role holds, computed by
//                                   the same function every permission check calls
//   {}                              a player token: `sub` is the player (`ply_…`, `player:{id}`)
//
// An organisation the caller is not an active member of is `404`, as it is on every organisation
// route (spec/07 §6.1). A member whose role needs a second factor this session does not have is
// `403 second-factor-required` — the console could not open, so its socket does not either.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { OrganizationIdSchema } from "../ids/schema.js";
import { commonProblemResponses, jsonResponse, problemResponse } from "../openapi/responses.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { RealtimePrincipalKindSchema } from "../realtime/connection.js";
export const RealtimeTokenRequestSchema = z
    .object({
    /** A member token for this organisation. Absent: a player token. */
    organizationId: OrganizationIdSchema.optional(),
})
    .openapi('RealtimeTokenRequest', {
    description: 'Which principal the token is for: a member of this organisation, or (with nothing) the player.',
});
export const RealtimeTokenSchema = z
    .object({
    /** Send it as the `p.token` of the first frame (`auth.connect`), or of `auth.refresh`. */
    token: z.string().min(1),
    kind: RealtimePrincipalKindSchema,
    /** `mem_…` or `ply_…`: the id in the principal's own channel. */
    principalId: z.string().min(1),
    expiresAt: IsoDateTimeSchema,
})
    .openapi('RealtimeToken', { description: 'A realtime token: five minutes, for the first frame on the socket.' });
export const createRealtimeTokenRoute = createRoute({
    method: 'post',
    path: '/v1/realtime/token',
    operationId: 'createRealtimeToken',
    tags: ['realtime'],
    summary: 'A realtime token for the socket: as a member of an organisation, or as the player',
    description: 'Needs a signed-in user: a session cookie in a browser, `Authorization: Bearer` in the apps. The token ' +
        'goes in the first frame on the socket, never in the URL (spec/09 §1). An organisation you are not an ' +
        'active member of is `404`; a role that needs a second factor this session lacks is `403`.',
    request: { body: { content: { 'application/json': { schema: RealtimeTokenRequestSchema } }, required: true } },
    responses: {
        200: jsonResponse(RealtimeTokenSchema, 'The realtime token.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: problemResponse('The role needs a second factor this session does not have.', 'second-factor-required'),
        404: problemResponse('No such organisation, or not one you are a member of.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const REALTIME_ROUTES = [createRealtimeTokenRoute];
//# sourceMappingURL=realtime.js.map